import { useCallback, useEffect, useRef, useState } from "react";
import * as ml from "../api/ml";
import { saveBlob } from "../api/export";
import { APIError } from "../api/errors";
import { compileActive, compileFilename, compileHeadline, compileProgress, compileReusable, POLL_MS } from "../lib/hailoCompile";
import { playCue } from "../lib/sound";
import { Dialog } from "../ui/Dialog";

/**
 * Compile the project's Hailo bundle to a .hef on the server's compiler machine. Opening it starts the compile (or picks up the one
 * already running for the project); closing it does not stop it: it goes on server-side and Export ▸ Compile shows where it got to.
 */
export function HailoCompileDialog({ projectId, projectName, version, calibLimit, onClose }: {
  projectId: number; projectName: string; version: number | null; calibLimit: number; onClose: () => void;
}) {
  const [job, setJob] = useState<ml.HailoCompileJob | null>(null);
  const [problem, setProblem] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  const timer = useRef<number | undefined>(undefined);
  /** What this dialog has already asked the server to start, so a second run of the effect (React's StrictMode in
   * development does one on purpose) never starts a second export. */
  const started = useRef("");
  /** A compile this dialog saw running: only its finish plays a sound, not opening the dialog on an old result. */
  const wasActive = useRef(false);

  const watch = useCallback((id: number) => {
    window.clearTimeout(timer.current);
    const tick = async () => {
      try {
        const { job: latest } = await ml.hailoCompileInfo(projectId);
        if (!alive.current) return;
        if (latest && latest.id === id) {
          setJob(latest);
          if (!compileActive(latest)) {
            if (wasActive.current) { wasActive.current = false; if (latest.status === "completed") playCue("ready"); else if (latest.status === "failed") playCue("problem"); }
            return;
          }
          wasActive.current = true;
        }
        setProblem("");
      } catch (e) {
        if (!alive.current) return;
        setProblem((e as Error).message);       // a hiccup: the compile is unaffected, so keep asking
      }
      timer.current = window.setTimeout(() => void tick(), POLL_MS);
    };
    timer.current = window.setTimeout(() => void tick(), POLL_MS);
  }, [projectId]);

  /** Start a compile, or, when the server says one is already going (409), pick that one up. */
  const startOrAttach = useCallback(async (): Promise<ml.HailoCompileJob> => {
    try {
      return await ml.startHailoCompile(projectId, { version, calib_limit: calibLimit });
    } catch (e) {
      if (e instanceof APIError && e.status === 409) {
        const { job: running } = await ml.hailoCompileInfo(projectId);
        if (running && compileActive(running)) return running;
      }
      throw e;
    }
  }, [projectId, version, calibLimit]);

  useEffect(() => {
    alive.current = true;
    const key = `${projectId}:${version}:${calibLimit}`;
    if (started.current === key) return () => { alive.current = false; window.clearTimeout(timer.current); };
    started.current = key;
    void (async () => {
      try {
        const { enabled, job: existing } = await ml.hailoCompileInfo(projectId);
        if (!alive.current) return;
        if (!enabled) { setProblem("This server has no Hailo compiler connected."); return; }
        const current = existing && compileReusable(existing, version) ? existing : await startOrAttach();
        if (!alive.current) return;
        setJob(current);
        wasActive.current = compileActive(current);
        watch(current.id);
      } catch (e) {
        if (!alive.current) return;
        setProblem(e instanceof APIError ? e.message : (e as Error).message);
      }
    })();
    return () => { alive.current = false; window.clearTimeout(timer.current); };
  }, [projectId, version, calibLimit, watch, startOrAttach]);

  const cancel = async () => {
    if (!job) return;
    setBusy(true);
    try { setJob(await ml.cancelHailoCompile(projectId, job.id)); if (compileActive(job)) watch(job.id); }
    catch (e) { setProblem((e as Error).message); }
    finally { setBusy(false); }
  };

  const download = async () => {
    if (!job) return;
    setBusy(true);
    try { saveBlob(await ml.downloadHailoCompile(projectId, job.id), compileFilename(projectName, job)); setProblem(""); }
    catch (e) { setProblem((e as Error).message); }
    finally { setBusy(false); }
  };

  /** A fresh compile of the same model, after a finished, failed or cancelled one. */
  const again = async () => {
    setBusy(true);
    setProblem("");
    try {
      const next = await startOrAttach();
      setJob(next);
      wasActive.current = compileActive(next);
      watch(next.id);
    } catch (e) { setProblem((e as Error).message); }
    finally { setBusy(false); }
  };

  const active = job ? compileActive(job) : false;
  return (
    <Dialog title="Compile to .hef" onClose={onClose} width={560} testId="hailo-compile">
      {!job && !problem && <p className="muted" data-testid="hailo-headline">Exporting the Hailo bundle and sending it to the compiler…</p>}
      {job && (
        <>
          <p style={{ marginTop: 0 }} data-testid="hailo-headline">{compileHeadline(job)}</p>
          {active && (
            <div className="progress" role="progressbar" aria-valuenow={Math.round(compileProgress(job) * 100)} aria-valuemin={0} aria-valuemax={100} data-testid="hailo-progress">
              <i style={{ width: `${compileProgress(job) * 100}%` }} />
            </div>
          )}
          {job.status === "failed" || job.status === "cancelled" ? (job.error && <div className="error-text" style={{ whiteSpace: "pre-wrap", margin: "8px 0" }} data-testid="hailo-error">{job.error}</div>) : null}
          {active && <p className="muted">This takes up to an hour. You can close this window: the compile carries on, and Export ▸ Compile to .hef shows where it has got to.</p>}
          {job.status === "completed" && <p className="muted">The zip holds the .hef and the files the ctrlX detector app needs beside it (metadata.json, dgqp.npz, classes.txt). Upload them together.</p>}
          {job.log_tail && (
            <details className="log-details" open={job.status === "failed"}>
              <summary>Compiler log</summary>
              <pre className="logview" data-testid="hailo-log">{job.log_tail}</pre>
            </details>
          )}
        </>
      )}
      {problem && <div className="error-text" style={{ whiteSpace: "pre-wrap", margin: "8px 0" }} data-testid="hailo-problem">{problem}</div>}
      <div className="dialog-actions">
        {active && <button className="btn" disabled={busy} onClick={() => void cancel()} data-testid="hailo-cancel">Cancel compile</button>}
        {job && !active && <button className="btn" disabled={busy} onClick={() => void again()} data-testid="hailo-again">Compile again</button>}
        {job?.status === "completed" && <button className="btn primary" disabled={busy} onClick={() => void download()} data-testid="hailo-download">Download</button>}
        <button className="btn" onClick={onClose} data-testid="hailo-close">{active ? "Close" : "Done"}</button>
      </div>
    </Dialog>
  );
}
